<template>
  <div>
    <div>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="leftLayoutConfig"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar
        class="app-layout-sidebar"
        :class="{ 'app-layout-sidebar--hidden': isSidebarMinimized && isOverlaySidebar }"
        :minimized="isSidebarMinimized"
        :animated="!isMobile"
        :mobile="isMobile"
        :tablet="isTablet"
      />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <AppLayoutNavigation v-if="!isMobile" class="p-4" />
      <main class="p-4 pt-0">
        <article>
          <RouterView />
        </article> 
      </main>
    </template>
  </VaLayout>
    </div>
  </div>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'

import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'
import { useImpersonationStore } from '../stores/impersonation'

const GlobalStore = useGlobalStore()
const impersonationStore = useImpersonationStore()

// Load impersonation state if this tab has a session
impersonationStore.loadFromSession()

const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(breakpoints.smDown)
const isTablet = ref(breakpoints.mdDown)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const isOverlaySidebar = computed(() => isMobile.value || isTablet.value)

const leftLayoutConfig = computed(() => ({
  fixed: true,
  absolute: isOverlaySidebar.value,
  order: 1,
  overlay: isOverlaySidebar.value && !isSidebarMinimized.value,
}))

const syncLayoutForViewport = (forceCloseDrawer = false) => {
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown

  if (forceCloseDrawer && breakpoints.mdDown) {
    isSidebarMinimized.value = true
  }

  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

// Avoid a first paint where overlay blocks the page on mobile
syncLayoutForViewport(true)

const onResize = () => {
  syncLayoutForViewport(false)
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}
</script>

<style lang="scss" scoped>
:deep(.app-layout-sidebar) {
  transform: translateX(0);
  opacity: 1;
  transition:
    width 0.25s ease,
    min-width 0.25s ease,
    transform 0.35s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.35s ease;
  will-change: transform, opacity, width;
}

/* Mobile / tablet drawer only — desktop keeps an icon rail when minimized */
:deep(.app-layout-sidebar--hidden) {
  transform: translateX(-100%);
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
}

@media screen and (max-width: 1023px) {
  /* Let taps reach page content when the drawer is closed */
  :deep(.va-layout__area--left) {
    pointer-events: none;
  }

  :deep(.va-layout__area--left .app-layout-sidebar:not(.app-layout-sidebar--hidden)) {
    pointer-events: auto;
    visibility: visible;
  }

  :deep(.app-layout-sidebar),
  :deep(.app-layout-sidebar.app-sidebar),
  :deep(.app-layout-sidebar.va-sidebar) {
    background: var(--va-background-primary) !important;
    background-color: var(--va-background-primary) !important;
    backdrop-filter: none !important;
    -webkit-backdrop-filter: none !important;
  }

  :deep(.app-layout-sidebar:not(.app-layout-sidebar--hidden)) {
    opacity: 1 !important;
  }
}
</style>
